import styled from "styled-components";
import HeroSection from "../HeroSection/HeroSection";
import MetricsBand from "./MetricsBand";
import Experience_v2 from "../Experience_v2/Experience_v2";
import Services from "../Services/Services";
import ProductsTeaser from "../Products/ProductsTeaser";
import ProjectsTeaser from "../Projects/ProjectsTeaser";
import Certifications from "../certifications/nonCredly/NonCredlyCertifications";
import Skills from "../Skills/Skills";
import Education_v2 from "../Education_v2/Education_v2";
import HighlightedStories from "../Stories/HighlightedStories";
import MartialArtsTeaser from "../MartialArts/MartialArtsTeaser";
import WorkshopsTeaser from "../Workshops/WorkshopsTeaser";
import DashboardTeaser from "../Dashboard/DashboardTeaser";
import AiInnovationTeaser from "../AiInnovation/AiInnovationTeaser";
import AchievementsTeaser from "../Achievements/AchievementsTeaser";

/**
 * ============================================================================
 * Home — the "/" route.
 * ============================================================================
 *
 * Extracted verbatim from `App.tsx` when routing was introduced for /stories:
 * same sections, same order, same wrappers, so "/" renders exactly what it
 * rendered before (Constitution II.5). App.tsx now owns only the chrome that
 * every route shares — Navbar, Footer, and the page background.
 *
 * NOTE: when re-enabling a commented-out section below, re-add its import.
 * `<Projects />` and `<ProjectDetails />` additionally need the `openModal`
 * state that used to live in App.tsx; it was dropped with this extraction
 * because nothing but a stray `console.log` still read it. Recoverable from
 * git history at 594c4ee.
 */

/* The magenta/blue corner wash behind Services, Platforms, Experience, Skills
   and Education. Tokenised in D-035: unchanged in dark, and at a third of the
   opacity in light, where a 15% magenta veil over porcelain reads as a printing
   fault rather than as atmosphere.

   ===========================================================================
   THE WASH AND ITS ANGLED FOOT LIVE ON A DECORATIVE LAYER, NOT ON THIS
   ELEMENT — D-049, and this is a defect fix, not a refactor.
   ===========================================================================
   `clip-path` on the wrapper clipped its CONTENT, and the notch was written as
   a PERCENTAGE of a block whose height has since tripled. `30% 98%` takes a
   bite 2% of the wrapper's height deep, and the wrapper is now 13,162px tall at
   1280 and 25,497px at 390 — so the "subtle" 2% bevel is a 263px to 510px wedge
   cut out of whatever happens to sit at the bottom. Skills and Education were
   appended inside this wrapper in D-038, which put the Education list exactly
   there. Measured on the built artifact: at 1280 in dark the PGDCA card was
   sliced through its notes line and the B.Sc card was gone entirely; in light
   BOTH of the last two entries were gone, and because the clipped region shows
   the near-identical page ground, the loss read as whitespace rather than as
   damage. Two of four education entries did not render — a section shipping
   less than the content layer holds, which is the II.4 failure inverted.

   Moving the paint to an `::before` fixes the class, not the instance: the
   shape is byte-identical (same gradient, same polygon, same 2%), and it can
   never again clip anything, however much content the wrapper grows. A
   decorative shape that clips content is a bug waiting for the next section.

   `isolation: isolate` is load-bearing. It makes this element a stacking
   context so the `z-index: -1` layer sits behind the sections but still in
   front of `Body`'s background — without it, a negative z-index escapes to the
   nearest ancestor stacking context and the wash disappears behind the page
   ground entirely. */
const Wrapper = styled.div`
	position: relative;
	isolation: isolate;
	width: 100%;

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		z-index: -1;
		background: var(--grad-wash);
		/* THE NOTCH IS A FIXED DEPTH, NOT A PERCENTAGE — owner ruling, D-062.
		   The vertex was 30% 98%, taking a bite 2% of the wrapper's height, and
		   this wrapper is 13,208px tall at 1280 and 25,470px at 390 — so "2%"
		   was a 264px to 509px wedge, a large hard-edged shape rather than the
		   detail the percentage was chosen to describe. The percentage was
		   never wrong as a NUMBER; it was wrong as a UNIT, because it makes a
		   decorative constant scale with however much content the page grows.
		   A calc() of 100% minus 40px reads the same at every page height and
		   at every width, which is what a detail has to do.
		   It stays on the ::before (D-049) and that is the load-bearing part:
		   the depth is a design choice now rather than a hazard, because a
		   clip-path on this layer cannot reach the content whatever value it
		   carries. */
		clip-path: polygon(0 0, 100% 0, 100% 100%, 30% calc(100% - 40px), 0 100%);
		pointer-events: none;
	}
`;

const Home = (): JSX.Element => (
	<>
		<HeroSection />
		{/* D-071. Directly under the hero because these are the figures a
		    recruiter scans for, and because they are the hero's evidence rather
		    than a separate topic — hence an eyebrow and no second heading. */}
		<MetricsBand />
		{/* ------------------------------------------------------------------
		    THE BROADCAST AND THE AI WORK LEAD THE PAGE — owner instruction,
		    2026-09-05 (D-107). Both sat at the BOTTOM: Recognition was the last
		    of fifteen sections and AI & Innovation the thirteenth, so the two
		    things the owner most wants seen were the two a reader reached last.

		    THEY WERE PUT THERE BY REASONING, NOT BY ACCIDENT, AND THE REASONING
		    IS WORTH KEEPING EVEN THOUGH IT LOST. D-069 placed Recognition last
		    because it is recognition OF the work above it, and D-095 placed AI
		    after Workshops because that strip gathers items out of
		    Certifications, Experience, Stories, Projects, Skills, Services and
		    Workshops — so it read once every one of its sources had. Both are
		    good arguments about a document read top to bottom. Neither was an
		    owner ruling; both were this assistant's, and D-106 is the record of
		    what happens when that distinction stops being made — an assistant's
		    call ages quietly into something that reads like policy.

		    WHAT THE ARGUMENTS MISSED is that Home is not read top to bottom. It
		    is scanned, by a recruiter, from the top, and the strongest claim on
		    it — a national broadcaster filming the work — was below fourteen
		    other sections. A synthesis that nobody scrolls to is not a
		    synthesis.

		    RECOGNITION BEFORE AI, because external validation is a stronger
		    opening than self-description, and because both then sit directly on
		    the hero's own evidence in `MetricsBand`. The forward reference the
		    AI strip now makes is what a lead is: it names credentials and builds
		    the reader has not met yet and sends them to the route that holds
		    them.

		    MOVED, NOT COPIED. A second copy on one page is the padding D-095
		    refused for the metrics and D-100 refused for the timeline, and it
		    would give `/ai-innovation` two Home entry points that could drift.
		    `home-order.mjs` asserts the position against the BUILT document, so
		    this is a fact about the page rather than about this file.
		    ------------------------------------------------------------------ */}
		<AchievementsTeaser />
		<AiInnovationTeaser />
		{/* One consolidated credentials section. The page previously carried
		    three stacked headings that all said "certifications". */}
		<Certifications />
		<Wrapper>
			{/* Services before Platforms by owner ruling (D-031): what is offered
			    reads first, what was built substantiates it. */}
			<Services />
			{/* D-067: the single Platforms section became two strips, each linking
			    to its own pre-rendered route. Products first — an owned suite is a
			    stronger claim than client delivery. */}
			<ProductsTeaser />
			<ProjectsTeaser />
			{/* <Experience /> */}
			<Experience_v2 />
			{/* Skills reads as the distillation of the roles just read, which is
			    why it follows Experience rather than preceding it — the reader
			    arrives with the evidence already in hand. */}
			<Skills />
			{/* Education closes the biography block as its foundation: the page
			    descends from what is offered, through what was built and where,
			    to what it amounts to, to where it started. Inside the Wrapper so
			    both sections keep the wash. */}
			<Education_v2 />
		</Wrapper>
		{/* D-095. Immediately after the biography block, because that is what it
		    summarises: the reader has just come down through Services, the two
		    platform strips, Experience, Skills and Education, and this draws
		    exactly that span. It deliberately does NOT repeat the eight headline
		    figures — `MetricsBand` sets those under the hero, and the same
		    numbers twice on one page with a link between them reads as padding.
		    Outside the Wrapper for the same reason as everything below: the
		    clip-path cuts its own bottom edge. */}
		<DashboardTeaser />
		{/* Sits outside the Wrapper deliberately: the Wrapper's clip-path cuts
		    its own bottom edge, and the strip is a separate movement rather than
		    the tail of the Experience block. */}
		<HighlightedStories />
		{/* D-080. Between the stories and the martial arts: teaching is still
		    professional work, so it reads on the working side of the page rather
		    than beside the discipline that is not. `/workshops` shipped in D-076
		    reachable only from a dropdown — every other route has a strip here. */}
		<WorkshopsTeaser />
		<MartialArtsTeaser />
		{/* <Projects openModal={openModal} setOpenModal={setOpenModal} /> */}
		{/* <Wrapper> */}
		{/* <Contact /> */}
		{/* </Wrapper> */}
		{/* <CallToAction /> */}
		{/* {openModal.state && <ProjectDetails openModal={openModal} setOpenModal={setOpenModal} />} */}
	</>
);

export default Home;
